import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";

import Profile from "./Profile";
import MyOrders from "./MyOrders";
import MyAddress from "./MyAddress";
import MyWishList from "./MyWishList";
import MyWallet from "./MyWallet";

import {
  FaUser,
  FaShoppingBag,
  FaMapMarkerAlt,
  FaHeart,
  FaSignOutAlt,
  FaChevronRight,
  FaWallet,
} from "react-icons/fa";

const Dashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [themeColor, setThemeColor] = useState("#ffffff");
  const [font, setFont] = useState("");

  // DESKTOP: which page to show
  const [activeSection, setActiveSection] = useState(
    location.state?.page || "profile"
  );

  // MOBILE: dropdown state (open/close)
  const [openSection, setOpenSection] = useState(null);

  const toggleSection = (section) => {
    setOpenSection(openSection === section ? null : section);
    setActiveSection(section); // Desktop content sync
  };

  useEffect(() => {
    const storedStyle = sessionStorage.getItem("UIStyle");
    if (storedStyle) {
      const parsedStyle = JSON.parse(storedStyle);
      setThemeColor(parsedStyle.web_theme_colour);
      setFont(parsedStyle.web_font);
    }
  }, []);

  useEffect(() => {
    if (location.state?.page) {
      setActiveSection(location.state.page);
    }
  }, [location.state]);

  return (
    <div className={`flex flex-col min-h-screen ${font}`}>

      {/* DESKTOP NAVBAR */}
      <nav className="items-center justify-between hidden w-full p-4 px-20 text-black bg-gray-100 md:flex">
        <h1 className="text-xl">Dashboard</h1>

        <div className="flex items-center gap-4">
          <div className="text-sm text-gray-500">
            <Link
              to="/"
              className="text-gray-500 no-underline hover:text-gray-700"
            >
              Home
            </Link>
            <span className="mx-1">›</span>
            <span className="font-medium" style={{ color: themeColor }}>
              Dashboard
            </span>
          </div>
        </div>
      </nav>

      <div className="flex flex-col flex-1 md:flex-row">

        {/* SIDEBAR ALWAYS VISIBLE */}
        <aside className="w-full md:w-64 bg-white text-black">
          <nav className="sticky top-0 flex flex-col min-h-screen space-y-2 border-r-2 border-gray-200">

            {/* PROFILE */}
            <button
              onClick={() => toggleSection("profile")}
              className={`py-4 mt-4 px-4 rounded-md text-left ${
                activeSection === "profile" ? "bg-gray-200" : "hover:bg-gray-300"
              }`}
              style={{
                color: activeSection === "profile" ? themeColor : "#4B5563",
              }}
            >
              <span className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <FaUser /> Profile
                </span>
                <FaChevronRight />
              </span>
            </button>

            {openSection === "profile" && (
              <div className="md:hidden p-2 bg-gray-100 rounded-lg">
                <Profile />
              </div>
            )}

            {/* ORDERS */}
            <button
              onClick={() => toggleSection("myorders")}
              className={`py-4 px-4 rounded-md text-left ${
                activeSection === "myorders"
                  ? "bg-gray-200"
                  : "hover:bg-gray-300"
              }`}
              style={{
                color: activeSection === "myorders" ? themeColor : "#4B5563",
              }}
            >
              <span className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <FaShoppingBag /> My Orders
                </span>
                <FaChevronRight />
              </span>
            </button>

            {openSection === "myorders" && (
              <div className="md:hidden p-2 bg-gray-100 rounded-lg">
                <MyOrders />
              </div>
            )}

            {/* ADDRESS */}
            <button
              onClick={() => toggleSection("myaddress")}
              className={`py-4 px-4 rounded-md text-left ${
                activeSection === "myaddress"
                  ? "bg-gray-200"
                  : "hover:bg-gray-300"
              }`}
              style={{
                color: activeSection === "myaddress" ? themeColor : "#4B5563",
              }}
            >
              <span className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <FaMapMarkerAlt /> Delivery Address
                </span>
                <FaChevronRight />
              </span>
            </button>

            {openSection === "myaddress" && (
              <div className="md:hidden p-2 bg-gray-100 rounded-lg">
                <MyAddress />
              </div>
            )}

            {/* WISHLIST */}
            <button
              onClick={() => toggleSection("mywishlist")}
              className={`py-4 px-4 rounded-md text-left ${
                activeSection === "mywishlist"
                  ? "bg-gray-200"
                  : "hover:bg-gray-300"
              }`}
              style={{
                color: activeSection === "mywishlist" ? themeColor : "#4B5563",
              }}
            >
              <span className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <FaHeart /> My Wishlist
                </span>
                <FaChevronRight />
              </span>
            </button>

            {openSection === "mywishlist" && (
              <div className="md:hidden p-2 bg-gray-100 rounded-lg">
                <MyWishList />
              </div>
            )}

            {/* WALLET */}
            <button
              onClick={() => toggleSection("mywallet")}
              className={`py-4 px-4 rounded-md text-left ${
                activeSection === "mywallet"
                  ? "bg-gray-200"
                  : "hover:bg-gray-300"
              }`}
              style={{
                color: activeSection === "mywallet" ? themeColor : "#4B5563",
              }}
            >
              <span className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <FaWallet /> My Wallet
                </span>
                <FaChevronRight />
              </span>
            </button>

            {openSection === "mywallet" && (
              <div className="md:hidden p-2 bg-gray-100 rounded-lg">
                <MyWallet />
              </div>
            )}

            {/* LOGOUT */}
            <button
              onClick={() => {
                sessionStorage.removeItem("accessToken");
                navigate("/");
                setTimeout(() => window.location.reload(), 100);
              }}
              className="px-4 py-3 text-left rounded-md hover:bg-gray-300"
              style={{ color: "#4B5563" }}
            >
              <span className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <FaSignOutAlt /> Log-out
                </span>
                <FaChevronRight />
              </span>
            </button>
          </nav>
        </aside>

        {/* DESKTOP MAIN CONTENT */}
        <main className="flex-1 hidden md:block">
          {activeSection === "profile" && <Profile />}
          {activeSection === "myorders" && <MyOrders />}
          {activeSection === "myaddress" && <MyAddress />}
          {activeSection === "mywishlist" && <MyWishList />}
          {activeSection === "mywallet" && <MyWallet />}
        </main>
      </div>
    </div>
  );
};

export default Dashboard;
